import React, { useMemo, useState, useEffect } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ButtonSelectFilter,
  CheckBoxFilter,
  TagsFilter,
  TextFilter,
} from "ui/ecosystems/filters";

import { MediaFilters, MediaSource } from "shared/types/files";
// TODO: most probably should not export from features/media-library here
// need to discuss with Ilya Agarkov to find out the best aproach
import { LibraryPayWall } from "features/media-library/components/library-pay-wall";
import { useCurrentUser } from "@/entities/current-user";
import { ALL_FILTERS_DATA, ALL_FILTERS_TYPE } from "constants/MediaLibrary";
import { Button } from "@/components/ui/button";

interface useMediaFilesFiltersProps {
  values: MediaFilters;
  tags: string[];
  permanentFilters?: FilterType[];

  onChange(value: any): void;

  onClear(): void;
}

type FilterType = keyof MediaFilters;

export interface MediaFilesFiltersProps {
  values: MediaFilters;
  tags: string[];
  availableFilters?: typeof ALL_FILTERS_TYPE;
  filtersList: (keyof MediaFilters)[];
  permanentFilters?: FilterType[];

  onAdd(name: FilterType): void;

  onChange(filterName: FilterType, value: unknown): void;

  onRemove(filterName: FilterType, value: unknown): void;

  onClear(): void;
}

export function MediaFilesSelectedFilters(
  props: MediaFilesFiltersProps & { hiddenFilters?: FilterType[] }
) {
  // filtersList - active/selected filters
  const {
    filtersList,
    onChange,
    onRemove,
    onClear,
    permanentFilters = [],
  } = props;
  const user = useCurrentUser();
  const usageOptions =
    user.role === "admin" ? usageAdminOptions : usageCollaboratorOptions;

  // Keyboard shortcut for clearing filters
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Shift + X to clear all filters
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "X") {
        e.preventDefault();
        if (filtersList.length > 0) {
          onClear();
        }
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [filtersList.length, onClear]);

  const renderFilter = (filter: keyof MediaFilters) => {
    const isPermanent = permanentFilters.includes(filter);

    let filterComponent: React.ReactNode = null;

    switch (filter) {
      case "name":
        filterComponent = (
          <TextFilter
            value={props.values.name}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            label="Name"
            placeholder="Asset name"
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "description":
        filterComponent = (
          <TextFilter
            value={props.values.description ?? ""}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            label="Description"
            placeholder="Asset description"
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "sourceName":
        filterComponent = (
          <ButtonSelectFilter
            value={props.values.sourceName ?? null}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            options={sourceNameOptions}
            label="Source"
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "tags":
        filterComponent = (
          <TagsFilter
            value={props.values.tags}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            label="Tags"
            placeholder="Select Tags"
            tags={props.tags}
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "excludeAccounts":
        filterComponent = (
          <TagsFilter
            value={props.values.excludeAccounts}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            label="Exclude Accounts"
            placeholder="Select Accounts"
            tags={props.tags}
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "favorite":
        filterComponent = (
          <CheckBoxFilter
            value={props.values.favorite}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            label="Starred"
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "mediaType":
        filterComponent = (
          <ButtonSelectFilter
            value={props.values.mediaType}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            options={fileTypeOptions}
            label="File type"
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      case "usage":
        filterComponent = (
          <ButtonSelectFilter
            value={props.values.usage}
            onChange={(value) => onChange(filter, value)}
            onRemove={
              isPermanent ? undefined : (value) => onRemove(filter, value)
            }
            options={usageOptions}
            label="Usage"
            formatLabel={(value: string | null) => {
              if (!value) return "Usage";
              const option = usageOptions?.find((item) => item.value === value);
              return `Used: ${option?.label || ""}`;
            }}
            icon={FILTER_ICONS[filter]}
          />
        );
        break;
      default:
        return null;
    }

    return (
      <div key={filter} className="animate-in fade-in duration-150">
        {filterComponent}
      </div>
    );
  };

  // Show clear button when there are removable filters OR when any filter has an active value
  const hasRemovableFilters = filtersList.some(
    (f) => !permanentFilters.includes(f)
  );
  const hasActiveValues = filtersList.some((f) => {
    const val = props.values[f];
    if (Array.isArray(val)) return val.length > 0;
    return Boolean(val);
  });
  const showClearButton = hasRemovableFilters || hasActiveValues;

  return (
    <>
      {filtersList
        .filter((filter) => !props.hiddenFilters?.includes(filter))
        .map(renderFilter)}

      {showClearButton && (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={onClear}
          data-testid="filterClearAllButton"
          className="h-7 px-3 text-xs font-medium text-neutral-600 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <i className="fa-solid fa-xmark mr-1.5" />
          Clear all filters
        </Button>
      )}
    </>
  );
}

// Filter icon mapping
const FILTER_ICONS: Record<string, string> = {
  name: "fa-regular fa-text",
  description: "fa-regular fa-align-left",
  sourceName: "fa-regular fa-cloud-arrow-down",
  tags: "fa-regular fa-tag",
  excludeAccounts: "fa-regular fa-user-slash",
  favorite: "fa-regular fa-star",
  mediaType: "fa-regular fa-file-image",
  usage: "fa-regular fa-chart-simple",
};

// Filter categories for better organization
const FILTER_CATEGORIES = {
  "Text Search": ["name", "description"],
  "Source & Type": ["sourceName", "mediaType", "usage"],
  Organization: ["tags", "excludeAccounts", "favorite"],
};

const getCategoryForFilter = (filterType: string): string => {
  for (const [category, filters] of Object.entries(FILTER_CATEGORIES)) {
    if (filters.includes(filterType)) {
      return category;
    }
  }
  return "Other";
};

export function MediaFilesFilters(props: MediaFilesFiltersProps) {
  // filtersList - active/selected filters
  const {
    filtersList,
    onAdd,
    availableFilters = ALL_FILTERS_TYPE,
    permanentFilters = [],
  } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Don't count permanent filters in the badge
  const addedFilterCount = filtersList.filter(
    (f) => !permanentFilters.includes(f)
  ).length;

  const filters = useMemo(
    () =>
      ALL_FILTERS_DATA.filter((filter) => {
        return availableFilters.includes(filter.type);
      }),
    [availableFilters]
  );

  const availableToAdd = filters.filter(
    (filter) => !filtersList.includes(filter.type)
  );

  // Group filters by category
  const filtersByCategory = useMemo(() => {
    const grouped: Record<string, typeof availableToAdd> = {};
    availableToAdd.forEach((filter) => {
      const category = getCategoryForFilter(filter.type);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(filter);
    });
    return grouped;
  }, [availableToAdd]);

  const categoryOrder = ["Text Search", "Source & Type", "Organization"];

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to open filter dropdown
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsDropdownOpen((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filtersList.length < filters.length && (
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant={
                addedFilterCount > 0 ? "blue-secondary" : "black-secondary"
              }
              size="sm"
              className="relative h-8 font-inter text-sm font-medium"
            >
              <i className="fa-solid fa-filter text-xs mr-1.5" />
              Filters
              {addedFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-500 rounded-full border-2 border-white">
                  {addedFilterCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[220px]">
            {categoryOrder.map((category) => {
              const categoryFilters = filtersByCategory[category];
              if (!categoryFilters || categoryFilters.length === 0) return null;

              return (
                <div key={category}>
                  <div className="px-2 py-1.5 text-xs font-medium text-neutral-600">
                    {category}
                  </div>
                  {categoryFilters.map((filter) => (
                    <LibraryPayWall
                      shouldBeNotDisabled={filter.free}
                      key={filter.type}
                    >
                      {({ isDisabled }) => (
                        <DropdownMenuItem
                          data-testid={`media-library-filters-${filter.type}`}
                          onClick={() => onAdd(filter.type)}
                          disabled={isDisabled}
                          className="pl-8 text-xs"
                        >
                          <i
                            className={`${
                              FILTER_ICONS[filter.type] ||
                              "fa-regular fa-filter"
                            } text-xs mr-2 text-neutral-400 absolute left-2`}
                          />
                          <span>{filter.label}</span>
                        </DropdownMenuItem>
                      )}
                    </LibraryPayWall>
                  ))}
                  {category !== categoryOrder[categoryOrder.length - 1] && (
                    <div className="my-1 border-t border-neutral-200" />
                  )}
                </div>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export function useMediaFilesFilters(
  props: useMediaFilesFiltersProps
): [Omit<MediaFilesFiltersProps, "availableFilters">, { clear(): void }] {
  const permanentFilters = props.permanentFilters || [];
  const [filtersList, { add, remove, clear }] = useFiltersList(
    props.values,
    permanentFilters
  );

  function handleChange(filterName: FilterType, value: unknown) {
    props.onChange({
      ...props.values,
      [filterName]: value,
    });
  }

  function handleRemove(filterName: FilterType, value: unknown) {
    handleChange(filterName, value);
    remove(filterName);
  }

  function handleClear() {
    props.onClear();
    clear();
  }

  return [
    {
      values: props.values,
      tags: props.tags,
      filtersList,
      permanentFilters,
      onAdd: add,
      onChange: handleChange,
      onRemove: handleRemove,
      onClear: handleClear,
    },
    { clear: handleClear },
  ];
}

function useFiltersList(
  presettedFilters: MediaFilters,
  permanentFilters: FilterType[] = []
): [
  FilterType[],
  {
    add(value: FilterType): void;
    remove(value: FilterType): void;
    clear(): void;
  },
] {
  const [filters, setFilters] = useState<FilterType[]>(() => {
    const defaultFilters: FilterType[] = [...permanentFilters];

    if (!presettedFilters) {
      return defaultFilters;
    }

    for (const key in presettedFilters) {
      const filterKey = key as FilterType;
      // Skip permanent filters (already added above) and sort key
      if (permanentFilters.includes(filterKey)) continue;
      if (
        (!/sort|tags|excludeAccounts/.test(filterKey) &&
          presettedFilters[filterKey]) ||
        (filterKey === "tags" && presettedFilters.tags.length) ||
        (filterKey === "excludeAccounts" &&
          presettedFilters.excludeAccounts.length)
      ) {
        defaultFilters.push(filterKey);
      }
    }
    return defaultFilters;
  });

  function add(name: FilterType) {
    if (!filters.includes(name)) {
      setFilters([...filters, name]);
    }
  }

  function remove(name: FilterType) {
    // Don't allow removing permanent filters
    if (permanentFilters.includes(name)) return;
    setFilters(filters.filter((filter) => filter !== name));
  }

  function clear() {
    // Keep permanent filters when clearing
    setFilters([...permanentFilters]);
  }

  return [filters, { add, remove, clear }];
}

export const sourceNameOptions = [
  {
    label: "Uploaded to library",
    value: MediaSource.upload,
  },
  {
    label: "Instagram feed (unknown)",
    value: MediaSource.feed,
  },
  {
    label: "Instagram Hashtag",
    value: MediaSource.instagramHashTags,
  },
  {
    label: "Instagram Tagged in",
    value: MediaSource.instagramTagged,
  },
  {
    label: "Zapier",
    value: MediaSource.zapier,
  },
  {
    label: "Chrome extension",
    value: MediaSource.chromeExtension,
  },
  {
    label: "Content Collect",
    value: MediaSource.contentCollect,
  },
];

export const fileTypeOptions = [
  {
    label: "Image",
    value: "image",
  },
  {
    label: "Video",
    value: "video",
  },
  {
    label: "Document/PDF",
    value: "document",
  },
];

export const usageAdminOptions = [
  {
    label: "Used",
    value: "used",
  },
  {
    label: "Unused",
    value: "unused",
  },
  {
    label: "All",
    value: null,
  },
];

export const usageCollaboratorOptions = [
  {
    label: "Used (all accounts)",
    value: "used",
  },
  {
    label: "Unused (all accounts)",
    value: "unused",
  },
  {
    label: "Used (my accounts)",
    value: "used_own",
  },
  {
    label: "Unused (my accounts)",
    value: "unused_own",
  },
  {
    label: "All",
    value: null,
  },
];
